import React from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import { useTranslation } from "react-i18next";

import breakpoints from "../../../styles/breakpoints";
import { colors, header, fonts, transition } from "../../../styles/variables";
import { safeAreaAbsoluteTop } from "../../../styles/safeArea";
import TrayContainer from "../../atoms/TrayContainer";
import { I18N_NAMESPACES } from "../../../helpers/constants";

import { Input } from "../../atoms/Input";
import AddProductsToShoppingCartButton from "../../atoms/AddProductsToShoppingCartButton";

import cross from "../../../assets/images/icons/cross.svg";
import searchImg from "../../../assets/images/icons/search.svg";
import minusIcon from "../../../assets/images/icons/minus.svg";
import plusIcon from "../../../assets/images/icons/plus.svg";

export const StyledBoxTray = styled(TrayContainer)`
  background-color: ${colors.white};
  transition: ${transition.baseTransition};
  max-width: 500px;
  width: 100%;
  z-index: 2200;
  overflow-y: scroll;
  ${breakpoints.mdUp} {
    top: ${header.desktopHeight};
  }
  ${(props) =>
    !props.isOpened &&
    css`
      display: none;
    `}
`;

export const Title = styled.h1`
  font-size: 50px;
  font-family: ${fonts.fontInterstateUltraBlack};
  text-transform: uppercase;
  text-align: left;
  margin: 50px 0;
`;

export const Subtitle = styled.span`
  font-size: 15px;
  text-align: left;
  font-family: ${fonts.fontBauRegular};
`;

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  margin: 0 40px 80px;
  ${breakpoints.xsDown} {
    margin: 20px 20px 80px;
  }
  > h6 {
    font-family: ${fonts.fontInterstateBlackCompressed};
    font-size: 20px;
  }
`;

const CrossContainer = styled.div``;

const Cross = styled.img.attrs(() => ({ src: cross }))`
  height: 15px;
  width: 15px;
  position: absolute;
  ${safeAreaAbsoluteTop(20)}
  left: 30px;
  cursor: pointer;
`;

export const CloseButton = (props) => (
  <CrossContainer {...props}>
    <Cross />
  </CrossContainer>
);

const StyledSearchBar = styled.div`
  position: fixed;
  bottom: 0;
  height: 60px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.1);
  border-radius: 10px 10px 0 0;
  background-color: #fff;
  padding-right: 20px;
  flex: 1 0 auto;

  > img {
    height: 18px;
    width: 18px;
  }
`;

const StyledInput = styled(Input)`
  width: 100%;
  border: none;
  position: relative;
`;

export const SearchBar = ({ onChange }) => {
  const { t } = useTranslation(I18N_NAMESPACES.MY_ACCOUNT);
  return (
    <StyledSearchBar>
      <StyledInput
        defaultValue=""
        onChange={(e) => onChange(e.target.value)}
        placeholder={t("myAccount:grapes.search")}
      />
      <img src={searchImg} alt="" />
    </StyledSearchBar>
  );
};

SearchBar.propTypes = {
  onChange: PropTypes.func.isRequired,
};

const StyledRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 30px 0;
  font-size: 12px;
`;

export const PanelBtn = styled.button`
  display: inline-block;
  border: none;
  background-color: transparent;
  padding: 0;
`;

export const PanelRight = styled.div`
  display: flex;
  height: 100%;
  justify-content: space-between;
  align-items: center;
  max-width: 90px;
  width: 100%;
  ${breakpoints.iphoneX} {
    width: 40%;
  }
  ${breakpoints.iphone5} {
    width: 40%;
  }
`;

export const PanelBtnAddAndRemove = styled(PanelBtn)`
  display: inline-block;
  border: none;
  background-color: transparent;
  padding: 0;
  width: 30px;
  height: 30px;
  ${breakpoints.iphone5} {
    width: 25px;
    height: 25px;
  }
`;

export const Icon = styled.img`
  width: 100%;
  border: 1px solid black;
  border-radius: 20px;
  cursor: pointer;
`;

export const NumberOfPacks = styled.span`
  line-height: 30px;
  font-size: 12px;
`;

export const Label = styled.span`
  font-size: 14px;
  font-weight: bold;
`;

export const OptionWrapper = styled.div`
  margin-bottom: 90px;
`;

export const Option = ({ label, quantity = 1, removeSpecialPack, addSpecialPack }) => (
  <StyledRow>
    <Label>{label}</Label>
    <PanelRight>
      <PanelBtnAddAndRemove onClick={() => removeSpecialPack()}>
        <Icon src={minusIcon} alt="Remove one pack" />
      </PanelBtnAddAndRemove>
      <NumberOfPacks>{quantity}</NumberOfPacks>
      <PanelBtnAddAndRemove onClick={() => addSpecialPack()}>
        <Icon src={plusIcon} alt="Add one pack" />
      </PanelBtnAddAndRemove>
    </PanelRight>
  </StyledRow>
);

Option.propTypes = {
  label: PropTypes.string.isRequired,
  quantity: PropTypes.number.isRequired,
  removeSpecialPack: PropTypes.func.isRequired,
  addSpecialPack: PropTypes.func.isRequired,
};

export const AddToCartButton = styled(AddProductsToShoppingCartButton)`
  position: fixed;
  width: 80%;
  align-self: center;
  /* Clear the Android gesture nav bar on standalone — env() is 0 on
   * web so the button stays at bottom: 30px there. */
  bottom: calc(30px + env(safe-area-inset-bottom));
  ${breakpoints.mdUp} {
    width: 340px;
  }
`;

export default SearchBar;
